<template>
    <div class="module">
        <header class="module__header">
            <a
                class="back-btn"
                @click="goBack('admin-user-groups')"
            >
                <span class="icon-chevron-left" />
            </a>
            <h1 class="has-back-btn">Create User Group</h1>
        </header>

        <section class="module__body">
            <form
                class="full-width"
                @submit.prevent="createGroup"
            >
                <div class="form-group">
                    <label for="name">Name</label>
                    <input
                        id="name"
                        v-model="name"
                        data-test-id="group-name"
                        type="text"
                        required
                        maxlength="32"
                    />
                </div>

                <group-mobile-feature-toggle-field
                    :availableFeatures="allFeatures"
                    :selectedFeatures="selectedFeatures"
                    @toggled="toggleFeature($event)"
                ></group-mobile-feature-toggle-field>

                <div class="form-group multi-select">
                    <multi-select-field
                        :allItems="allUsers"
                        label="Users"
                        testId="users-select"
                        :selectedValues="selectedUserIdsArray"
                        @items-selected="handleUsersSelected"
                        @items-unselected="handleUsersUnselected"
                    ></multi-select-field>
                </div>

                <div class="form-group multi-select">
                    <multi-select-field
                        :allItems="allFrequencySets"
                        label="Frequency Sets"
                        testId="frequency-sets-select"
                        :selectedValues="selectedFrequencySetIdsArray"
                        @items-selected="handleFrequencySetsSelected"
                        @items-unselected="handleFrequencySetsUnselected"
                    >
                        <template #option-suffix="{ option }">
                            <frequency-set-type-badge
                                :type="option.type"
                                size="small"
                                :data-test-id="`frequency-set-type-${option.id}`"
                            />
                        </template>
                    </multi-select-field>
                </div>

                <form-errors :response="errorResponse"></form-errors>

                <div class="form-group">
                    <button
                        type="submit"
                        class="btn btn-primary"
                        data-test-id="group-create-button"
                        :class="{ active: submitted }"
                    >
                        <div>Create</div>
                    </button>
                </div>
            </form>
        </section>
    </div>
</template>

<script>
import FormErrors from '@/components/FormErrors.vue'
import GroupMobileFeatureToggleField from '@/components/GroupMobileFeatureToggleField.vue'
import canToast from '@/mixins/canToast'
import hasBackButton from '@/mixins/hasBackButton'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import MultiSelectField from '@/components/MultiSelectField.vue'
import FrequencySetTypeBadge from '@/components/FrequencySetTypeBadge.vue'

export default {
    components: {
        FormErrors,
        GroupMobileFeatureToggleField,
        MultiSelectField,
        FrequencySetTypeBadge,
    },

    mixins: [canToast, hasBackButton, needsOrganizationSlug],

    data() {
        return {
            name: '',
            allUsers: [],
            selectedUserIds: new Set(),
            allFrequencySets: [],
            selectedFrequencySetIds: new Set(),
            allFeatures: {},
            selectedFeatures: [],
            errorResponse: null,
            submitted: false,
        }
    },

    computed: {
        selectedUserIdsArray() {
            return Array.from(this.selectedUserIds)
        },

        selectedFrequencySetIdsArray() {
            return Array.from(this.selectedFrequencySetIds)
        },
    },

    mounted() {
        this.$http.get(`/${this.organizationSlug}/user-groups/groupable`).then(({ data }) => {
            this.allUsers = data.users.map((user) => ({
                id: user.id,
                name: (user.name || user.email) + (user.title ? ` (${user.title})` : ''),
            }))
            this.allFrequencySets = data.frequency_sets.map(({ id, name, type }) => ({
                id,
                name,
                type,
            }))
            this.allFeatures = data.mobile_features
        })
    },

    methods: {
        toggleFeature(feature) {
            if (this.isFeatureSelected(feature)) {
                for (let i = this.selectedFeatures.length - 1; i >= 0; i--) {
                    if (this.selectedFeatures[i].id === feature.id) {
                        this.selectedFeatures.splice(i, 1)
                    }
                }
            } else {
                this.selectedFeatures.push(feature)
            }
        },
        isFeatureSelected(feature) {
            const isLocked = Boolean(feature.organization_id === null && feature.is_premium)
            return !isLocked && this.selectedFeatures.some((selectedFeature) => selectedFeature.id === feature.id)
        },
        createGroup() {
            this.submitted = true
            const selectedFeatureIds = this.selectedFeatures.map((feature) => parseInt(feature.id))

            this.$http
                .post(`/${this.organizationSlug}/user-groups`, {
                    name: this.name,
                    users: this.selectedUserIdsArray,
                    frequency_sets: this.selectedFrequencySetIdsArray,
                    mobile_features: selectedFeatureIds,
                })
                .then(() => {
                    this.toast('User group successfully created!')
                    this.$router.push(`/${this.organizationSlug}/user-groups`)
                })
                .catch((error) => {
                    this.errorResponse = error.response
                    this.submitted = false
                })
        },
        handleUsersSelected(ids) {
            ids.forEach((id) => {
                this.selectedUserIds.add(id)
            })
        },
        handleUsersUnselected(ids) {
            ids.forEach((id) => {
                this.selectedUserIds.delete(id)
            })
        },
        handleFrequencySetsSelected(ids) {
            ids.forEach((id) => {
                this.selectedFrequencySetIds.add(id)
            })
        },
        handleFrequencySetsUnselected(ids) {
            ids.forEach((id) => {
                this.selectedFrequencySetIds.delete(id)
            })
        },
    },
}
</script>
